/**
 * TierMatrixBlocks — constants and sub-components shared by TierMatrixEditor.
 * Extracted to reduce TierMatrixEditor.jsx complexity.
 */

import React from 'react';
import {
  ChevronRight, ChevronDown,
  Layers, Grid3X3, Monitor,
  CheckSquare, Square, MinusSquare,
  Globe
} from 'lucide-react';

// ── Tier definitions ──────────────────────────────────────────────────────────
export const TIER_ORDER = [
  'Lite',
  'Express',
  'Standard',
  'Professional',
  'Enterprise',
];

export const TIER_COLORS = {
  'Lite':         '#10b981',
  'Express':      '#06b6d4',
  'Standard':     '#3b82f6',
  'Professional': '#6366f1',
  'Enterprise':   '#8b5cf6',
};

export const TIER_SHORT = {
  'Lite':         'Lite',
  'Express':      'Express',
  'Standard':     'Standard',
  'Professional': 'Pro',
  'Enterprise':   'Enterprise',
};

// ── Layout constants ──────────────────────────────────────────────────────────
export const COL_W  = 48;   // px width per tier column
export const ROW_H  = 36;   // px height for module/sub rows
export const SCR_H  = 30;   // px height for screen rows
export const HDR_H  = 130;  // px height for column-header row

// ── Tri-state checkbox ────────────────────────────────────────────────────────
export function TCB({ state, onClick, color, size = 15, testId }) {
  const Icon = state === 'all' ? CheckSquare : state === 'partial' ? MinusSquare : Square;
  return (
    <button
      onClick={onClick}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      data-testid={testId}
    >
      <Icon size={size} color={state === 'none' ? '#cbd5e1' : color} />
    </button>
  );
}

// ── ModuleBlock ───────────────────────────────────────────────────────────────
export function ModuleBlock({
  mod, selectedTier, expanded, onToggleExpand,
  onToggleModule, onToggleSubModule, onToggleScreen,
  moduleState, subModState, scrEnabled,
  tiers: propTiers, colors: propColors,
  // Common column — optional (undefined = hide column for legacy callers)
  commonModState, commonSubState, commonScrEnabled,
  onToggleCommonModule, onToggleCommonSubModule, onToggleCommonScreen,
}) {
  // Support both dynamic (new) and static-fallback (legacy) callers
  const tiers  = (propTiers  && propTiers.length  > 0) ? propTiers  : TIER_ORDER;
  const colors = (propColors && Object.keys(propColors).length > 0) ? propColors : TIER_COLORS;
  const hasCommon = !!commonModState;

  const COMMON_CELL = { textAlign: 'center', padding: 0, background: '#e0f2fe22', borderLeft: '2px solid #bae6fd', borderRight: '1px solid #bae6fd' };

  const isModEx = expanded.modules.has(String(mod.ModuleId));
  const subs    = mod.subModules || [];

  return (
    <>
      {/* Module row */}
      <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
        <td style={{
          position: 'sticky', left: 0, zIndex: 2,
          background: 'var(--surface-2, #f8fafc)',
          borderRight: '1px solid var(--border, #e2e8f0)',
          padding: '0 10px 0 6px', height: ROW_H,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <button
              onClick={() => onToggleExpand('modules', mod.ModuleId)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#64748b', flexShrink: 0 }}
              data-testid={`mod-exp-${mod.ModuleId}`}
            >
              {isModEx ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
            <Layers size={12} color="#6366f1" style={{ flexShrink: 0 }} />
            <span style={{ fontWeight: 700, fontSize: '0.93rem', color: 'var(--text-primary, #0f172a)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {mod.ModuleName}
            </span>
            <span style={{ fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0 }}>({subs.length})</span>
          </div>
        </td>
        {/* Common cell */}
        {hasCommon && (
          <td style={{ ...COMMON_CELL, height: ROW_H }}>
            <TCB state={commonModState(mod)} onClick={() => onToggleCommonModule(mod)} color="#0ea5e9" size={15} testId={`common-mod-${mod.ModuleId}`} />
          </td>
        )}
        {tiers.map(tier => {
          const color = colors[tier] || '#64748b';
          const isSel = selectedTier === tier;
          const state = moduleState(tier, mod);
          return (
            <td key={tier} style={{
              textAlign: 'center', height: ROW_H, padding: 0,
              background: isSel ? `${color}09` : 'transparent',
              borderLeft: `1px solid ${isSel ? `${color}20` : '#f1f5f9'}`,
            }}>
              <TCB state={state} onClick={() => onToggleModule(tier, mod)} color={color} size={15} testId={`mod-${mod.ModuleId}-${tier}`} />
            </td>
          );
        })}
      </tr>

      {/* SubModule rows */}
      {isModEx && subs.map(sub => {
        const isSubEx = expanded.subModules.has(String(sub.SubModuleId));
        const screens = sub.screens || [];
        return (
          <React.Fragment key={sub.SubModuleId}>
            <tr style={{ borderBottom: '1px solid #f8fafc' }}>
              <td style={{
                position: 'sticky', left: 0, zIndex: 2,
                background: 'var(--surface-1, #fff)',
                borderRight: '1px solid var(--border, #e2e8f0)',
                padding: '0 10px 0 26px', height: ROW_H - 2,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    onClick={() => onToggleExpand('subModules', sub.SubModuleId)}
                    disabled={screens.length === 0}
                    style={{ background: 'none', border: 'none', cursor: screens.length === 0 ? 'default' : 'pointer', padding: 2, color: '#94a3b8', flexShrink: 0, opacity: screens.length === 0 ? 0.25 : 1 }}
                    data-testid={`sub-exp-${sub.SubModuleId}`}
                  >
                    {isSubEx ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                  </button>
                  <Grid3X3 size={10} color="#94a3b8" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: '0.86rem', color: 'var(--text-secondary, #334155)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {sub.SubModuleName}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0 }}>({screens.length})</span>
                </div>
              </td>
              {/* Common cell */}
              {hasCommon && (
                <td style={{ ...COMMON_CELL, height: ROW_H - 2 }}>
                  <TCB state={commonSubState(sub)} onClick={() => onToggleCommonSubModule(sub)} color="#0ea5e9" size={13} testId={`common-sub-${sub.SubModuleId}`} />
                </td>
              )}
              {tiers.map(tier => {
                const color = colors[tier] || '#64748b';
                const isSel = selectedTier === tier;
                const state = subModState(tier, sub);
                return (
                  <td key={tier} style={{
                    textAlign: 'center', height: ROW_H - 2, padding: 0,
                    background: isSel ? `${color}06` : 'transparent',
                    borderLeft: `1px solid ${isSel ? `${color}15` : '#f8fafc'}`,
                  }}>
                    <TCB state={state} onClick={() => onToggleSubModule(tier, sub)} color={color} size={13} testId={`sub-${sub.SubModuleId}-${tier}`} />
                  </td>
                );
              })}
            </tr>

            {/* Screen rows */}
            {isSubEx && screens.map(scr => (
              <tr key={scr.SeqId} style={{ borderBottom: '1px solid #fafafa' }}>
                <td style={{
                  position: 'sticky', left: 0, zIndex: 2,
                  background: '#fff',
                  borderRight: '1px solid var(--border, #e2e8f0)',
                  padding: '0 10px 0 48px', height: SCR_H,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Monitor size={10} color="#94a3b8" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '0.79rem', color: 'var(--text-primary, #0f172a)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {scr.ScreenTitle}
                    </span>
                    {scr.ScreenMenuName && (
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace', flexShrink: 0 }}>{scr.ScreenMenuName}</span>
                    )}
                    {scr.ScreenType && (
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', background: '#f1f5f9', padding: '1px 4px', borderRadius: 3, flexShrink: 0 }}>{scr.ScreenType}</span>
                    )}
                  </div>
                </td>
                {/* Common cell */}
                {hasCommon && (
                  <td style={{ ...COMMON_CELL, height: SCR_H }}>
                    <TCB state={commonScrEnabled(scr.SeqId) ? 'all' : 'none'} onClick={() => onToggleCommonScreen(scr.SeqId)} color="#0ea5e9" size={12} testId={`common-scr-${scr.SeqId}`} />
                  </td>
                )}
                {tiers.map(tier => {
                  const color   = colors[tier] || '#64748b';
                  const isSel   = selectedTier === tier;
                  const enabled = scrEnabled(tier, scr.SeqId);
                  return (
                    <td key={tier} style={{
                      textAlign: 'center', height: SCR_H, padding: 0,
                      background: enabled && isSel ? `${color}10` : isSel ? `${color}04` : 'transparent',
                      borderLeft: `1px solid ${isSel ? `${color}18` : '#fafafa'}`,
                    }}>
                      <TCB
                        state={enabled ? 'all' : 'none'}
                        onClick={() => onToggleScreen(tier, scr.SeqId)}
                        color={color}
                        size={12}
                        testId={`scr-${scr.SeqId}-${tier}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}
